/**
 * Shared validation utilities for project migration
 */

import type { Project } from './types'

/**
 * Plan tier hierarchy for feature validation
 */
export const PLAN_ORDER = {
  free: 0,
  basic: 1,
  premium: 2,
  platinum: 3,
} as const

export type PlanTier = keyof typeof PLAN_ORDER

/**
 * Normalize plan tier from subscription plan string
 * @param plan - Plan string from subscription (e.g., 'premium_monthly')
 * @returns Normalized plan tier
 */
export function normalizePlanTier(plan?: string): PlanTier {
  const normalized = plan?.split('_')[0]?.toLowerCase()
  if (
    normalized === 'basic' ||
    normalized === 'premium' ||
    normalized === 'platinum' ||
    normalized === 'free'
  ) {
    return normalized
  }
  return 'free'
}

/**
 * Check if a plan meets the required tier
 * @param currentPlan - Current plan tier
 * @param requiredPlan - Required plan tier
 * @returns True if current plan meets or exceeds required plan
 */
export function hasRequiredPlan(
  currentPlan: PlanTier,
  requiredPlan: PlanTier,
): boolean {
  return PLAN_ORDER[currentPlan] >= PLAN_ORDER[requiredPlan]
}

/**
 * Project feature usage analysis
 */
export interface IProjectFeatures {
  usesCustomDomain: boolean
  usesSeo: boolean
  usesEcommerce: boolean
  usesCustomCode: boolean
}

/**
 * Analyze project features to determine migration requirements
 * @param project - Project to analyze
 * @param productsCount - Number of products in the project (requires DB query)
 * @returns Feature usage summary
 */
export function analyzeProjectFeatures(
  project: Project,
  productsCount: number,
): IProjectFeatures {
  return {
    usesCustomDomain: !!(
      project.custom_domain ||
      project.own_domain ||
      project.new_domain
    ),
    usesSeo: project.auto_seo_field?.enabled === true,
    usesEcommerce: productsCount > 0,
    usesCustomCode: !!(
      project.customCode?.endOfHead ||
      project.customCode?.endOfBody ||
      project.customCode?.ga ||
      project.customCode?.socialPixel ||
      project.customCode?.pinterestTag ||
      project.customCode?.tiktokPixel
    ),
  }
}

/**
 * Workspace compatibility result
 */
export interface IWorkspaceCompatibility {
  compatible: boolean
  reasons: string[]
  domainLimitIssue: boolean
  planIssue: boolean
}

/**
 * Validate if a project can be migrated to a workspace
 * @param features - Project feature usage
 * @param workspacePlan - Target workspace plan tier
 * @param workspaceHasAvailableDomains - Whether workspace has available domain slots
 * @returns Compatibility result with specific reasons for incompatibility
 */
export function validateMigrationCompatibility(
  features: IProjectFeatures,
  workspacePlan: PlanTier,
  workspaceHasAvailableDomains: boolean,
): IWorkspaceCompatibility {
  const reasons: string[] = []
  let domainLimitIssue = false
  let planIssue = false

  // Check domain availability
  if (features.usesCustomDomain && !workspaceHasAvailableDomains) {
    reasons.push('domain_limit_reached')
    domainLimitIssue = true
  }

  // Check SEO features (requires premium)
  if (features.usesSeo && !hasRequiredPlan(workspacePlan, 'premium')) {
    reasons.push('requires_seo_plan')
    planIssue = true
  }

  // Check e-commerce features (requires premium)
  if (features.usesEcommerce && !hasRequiredPlan(workspacePlan, 'premium')) {
    reasons.push('requires_ecommerce_plan')
    planIssue = true
  }

  // Check custom code features (requires premium)
  if (features.usesCustomCode && !hasRequiredPlan(workspacePlan, 'premium')) {
    reasons.push('requires_custom_code_plan')
    planIssue = true
  }

  return {
    compatible: reasons.length === 0,
    reasons,
    domainLimitIssue,
    planIssue,
  }
}
